import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import embedPdf from '../../../modifiers/embed-pdf.ts';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import { t } from 'ember-intl';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import Table from 'tio-ui/components/table';
import Button from 'tio-ui/components/button';
import { Request } from 'tio-data';
import { profileRecommendationsRequest } from 'tio-data/requests/profile-history';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import TioPreviewButton from '../../tio/preview-button.gts';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import type FeaturesService from '../../../services/features.ts';

interface ProfileRecommendationsTableSignature {
  Args: {
    /** Exact requesting application filter, such as PSLF, IDR or STRATEGY_FINDER. */
    recommendationType: string;
    user: UserModel;
    /** Maximum newest-first records; defaults to ten. */
    limit?: number;
  };
}

export default class ProfileRecommendationsTableComponent extends Component<ProfileRecommendationsTableSignature> {
  @service declare features: FeaturesService;

  @tracked showPreview = false;
  @tracked pdfSrc = '';

  // PDF blob downloads use direct fetch, so they need explicit auth headers.
  get tioAuthHeaders() {
    return buildTioAuthHeaders({
      apiKey: this.features.ENV?.apiKey ?? '',
    });
  }

  get recommendationsBaseUrl() {
    const opts: QueryUrlOptions = {
      identifier: { type: 'recommendations' },
      op: 'query',
      resourcePath: 'recommendations',
    };
    return buildBaseURL(opts);
  }

  showPreviewModal = async (recommendation: RecommendationModel) => {
    const res = await fetch(`${this.recommendationsBaseUrl}/${recommendation.id}.pdf`, {
      headers: this.tioAuthHeaders,
    });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    this.pdfSrc = url;
    this.showPreview = true;
  };

  closePreviewModal = () => {
    this.showPreview = false;
    this.pdfSrc = '';
  };

  <template>
    {{#if @user.id}}
      <Request @query={{profileRecommendationsRequest @user.id @recommendationType (or @limit 10)}}>
        <:error as |_error features|>
          <div role="alert" class="tio-col-tight" data-test-profile-recommendations-error>
            <p class="tio-fg-danger">{{t "common.records.load_error"}}</p>
            <Button
              @appearance="outlined"
              data-test-profile-recommendations-retry
              {{on "click" features.retry}}
            >{{t "common.records.retry"}}</Button>
          </div>
        </:error>
        <:always as |state|>
          {{#unless state.isError}}
            <Table class="p-4" @isLoading={{state.isPending}}>
              <:thead as |options|>
                <options.tr>
                  <options.th />
                  <options.th>{{t "common.recommendations.date"}}</options.th>
                  <options.th>{{t "common.recommendations.results"}}</options.th>
                </options.tr>
              </:thead>
              <:tbody as |options|>
                {{#each state.value.data as |result|}}
                  <options.tr data-test-profile-recommendation-row={{result.id}}>
                    <options.td>
                      <TioPreviewButton @onClick={{fn this.showPreviewModal result}} />
                    </options.td>
                    {{#let
                      (safeFormatDate result.createdAt month="long" day="2-digit" year="numeric")
                      as |date|
                    }}
                      <options.td title={{date}}>{{date}}</options.td>
                    {{/let}}
                    <options.td>{{result.bestPlanTableRowSummary}}</options.td>
                  </options.tr>
                {{else}}
                  <options.tr>
                    <options.td colspan="3">
                      {{t "common.recommendations.no_results"}}
                    </options.td>
                  </options.tr>
                {{/each}}
              </:tbody>
            </Table>
          {{/unless}}
        </:always>
      </Request>
    {{/if}}

    <Modal
      @isOpen={{this.showPreview}}
      @onClose={{this.closePreviewModal}}
      @ariaLabel="PDF Preview"
      @size="full"
    >
      <:body>
        {{#if this.pdfSrc}}
          <div {{embedPdf this.pdfSrc}}></div>
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onSubmit={{this.closePreviewModal}}
          @submitText="Close Preview"
        />
      </:footer>
    </Modal>
  </template>
}
